import {
  useAppInsightsContext,
  useTrackMetric,
} from '@microsoft/applicationinsights-react-js';
import groq from 'groq';
import type { GetStaticPaths, GetStaticProps } from 'next';
import { type ParsedUrlQuery } from 'querystring';
import { type ComponentPropsWithoutRef } from 'react';

import LibraryPage, {
  getLibraryPageStaticProps,
  LIBRARY_PAGE_ARTICLE_PROJECTION,
  NonCategorySlugs,
} from '../../components/library/LibraryPage';
import { TagPage } from '../../components/library/TagPage';
import Layout, {
  getStaticLayoutQuery,
  type LayoutDataProps,
} from '../../components/page/Layout';
import {
  type ArticleWithWrittenAt,
  BASE_ARTICLES_QUERY_FILTERS,
  CATEGORY_BASE_ARTICLES_QUERY_FILTERS,
  CATEGORY_LANDING_PAGE_SLUGS_GROQ,
  CATEGORY_QUERY,
  STORIES_BASE_ARTICLES_QUERY_FILTERS,
  TAG_BASE_ARTICLES_QUERY_FILTERS,
} from '../../lib/sanity/commonGroqs';
import { sanityClient } from '../../lib/sanity/sanityClient';
import {
  DEFAULT_PAGE_SIZE,
  paginateResults,
  type PaginationTransformer,
} from '../../utils/pagination';
import { getLibraryPageUrl } from '../../utils/routing';
import {
  FEATURED_TAG_SLUGS,
  FEATURED_TAG_SLUGS_TO_CMS_TAG,
} from '../../utils/tags';

export enum LibraryPageType {
  CATEGORY = 'CATEGORY',
  BASE = 'BASE',
  STORIES = 'STORIES',
  TAG = 'TAG',
}

const LIBRARY_PAGE_TYPE_TO_BASE_GROQ: Record<LibraryPageType, string> = {
  [LibraryPageType.CATEGORY]: CATEGORY_BASE_ARTICLES_QUERY_FILTERS,
  [LibraryPageType.BASE]: BASE_ARTICLES_QUERY_FILTERS,
  [LibraryPageType.STORIES]: STORIES_BASE_ARTICLES_QUERY_FILTERS,
  [LibraryPageType.TAG]: TAG_BASE_ARTICLES_QUERY_FILTERS,
};

const getFeaturedArticlesGroq = (pageType: LibraryPageType) => {
  return groq`*[${LIBRARY_PAGE_TYPE_TO_BASE_GROQ[pageType]} && ${
    pageType === LibraryPageType.BASE ? 'featuredHome' : 'featuredInCategory'
  }] {${LIBRARY_PAGE_ARTICLE_PROJECTION}} | order(writtenAt desc)`;
};

const getRecentArticlesGroq = (pageType: LibraryPageType) => {
  return groq`*[${LIBRARY_PAGE_TYPE_TO_BASE_GROQ[pageType]}] {${LIBRARY_PAGE_ARTICLE_PROJECTION}} | order(writtenAt desc) [$start...$end]`;
};

export const getTotalArticlesGroq = (pageType: LibraryPageType): string => {
  return groq`count(*[${LIBRARY_PAGE_TYPE_TO_BASE_GROQ[pageType]}])`;
};

type Props = Partial<
  Omit<ComponentPropsWithoutRef<typeof LibraryPage>, 'fetchMoreArticles'>
> & {
  goalId: Possible<string>;
  page: number;
  tag: Possible<string>;
  pageContentData: Possible<LayoutDataProps>;
};

const EMPTY_ARTICLES: ArticleWithWrittenAt[] = [];
const EMPTY_CATEGORIES_BY_GOAL: Props['categoriesByGoal'] = [];

const LibraryCategoryLandingPage: NextPageWithLayout<Props> = ({
  title = '',
  subtitle = '',
  featuredArticles = EMPTY_ARTICLES,
  recentArticles = EMPTY_ARTICLES,
  totalArticles = 0,
  activeSlug = '',
  categoriesByGoal = EMPTY_CATEGORIES_BY_GOAL,
  page,
  tag,
}) => {
  const appInsights = useAppInsightsContext();
  useTrackMetric(appInsights, 'LibraryPage', {
    activeSlug,
  });

  return tag ? (
    <TagPage
      articles={recentArticles}
      totalArticles={totalArticles}
      categorySlug={activeSlug}
      categoryTitle={title}
      tag={tag}
      page={page}
    />
  ) : (
    <LibraryPage
      // include a key to force rerender on navigation
      key={activeSlug}
      title={title}
      subtitle={subtitle}
      featuredArticles={featuredArticles}
      recentArticles={recentArticles}
      totalArticles={totalArticles}
      activeSlug={activeSlug}
      categoriesByGoal={categoriesByGoal}
      page={page}
    />
  );
};

LibraryCategoryLandingPage.getLayout = (
  page,
  {
    title = '',
    subtitle = '',
    totalArticles = 0,
    page: currentPage = 1,
    activeSlug,
    tag,
    pageContentData,
  },
) => {
  const canonicalLink = getLibraryPageUrl({
    slug: activeSlug,
    tag,
  });
  const next =
    currentPage * DEFAULT_PAGE_SIZE < totalArticles
      ? getLibraryPageUrl({
          slug: activeSlug,
          tag,
          page: currentPage + 1,
        })
      : undefined;
  const prev =
    currentPage > 1
      ? getLibraryPageUrl({
          slug: activeSlug,
          tag,
          page: currentPage - 1,
        })
      : undefined;
  return (
    <Layout
      title={title}
      description={subtitle}
      canonicalLink={canonicalLink}
      next={next}
      prev={prev}
      pageContentData={pageContentData}
    >
      {page}
    </Layout>
  );
};

interface CategoryPageStaticParams extends ParsedUrlQuery {
  libraryPagePath: string[];
}

const paginationTransformerFactory: (
  slug: Nullable<string>,
  tag?: Nullable<string>,
) => PaginationTransformer<CategoryPageStaticParams> =
  (slug, tag) => (page: number) => {
    const libraryPagePath = slug ? [slug] : [];
    if (tag) libraryPagePath.push(tag);
    if (page > 1) {
      libraryPagePath.push(page.toString());
    }
    return { libraryPagePath };
  };

export const getStaticPaths: GetStaticPaths = async () => {
  const paths: CategoryPageStaticParams[] = [];
  // index + pagination
  // do index query -> run common pagination logic
  const indexCount = await sanityClient.fetch<number>(
    getTotalArticlesGroq[LibraryPageType.BASE] as string,
  );
  const indexPageTransformer = paginationTransformerFactory(null);
  paths.push(
    ...paginateResults<CategoryPageStaticParams>({
      transformFn: indexPageTransformer,
      count: indexCount,
      pageSize: DEFAULT_PAGE_SIZE,
    }),
  );
  // stories + pagination
  // do stories query -> run common pagination logic
  const storiesCount = await sanityClient.fetch<number>(
    getTotalArticlesGroq(LibraryPageType.STORIES),
  );
  const storiesPageTransformer = paginationTransformerFactory('stories');
  paths.push(
    ...paginateResults({
      transformFn: storiesPageTransformer,
      count: storiesCount,
      pageSize: DEFAULT_PAGE_SIZE,
    }),
  );
  // categories + pagination
  // do get categories query => run common pagination logic for each category
  const categories = await sanityClient.fetch<
    Array<{
      slug?: { current?: string };
      categoryId: string;
      goalId: string;
    }>
  >(CATEGORY_LANDING_PAGE_SLUGS_GROQ);
  for (const category of categories) {
    const categorySlug = category.slug?.current ?? '';
    const categoryId = category.categoryId;
    const categoryCount = await sanityClient.fetch<number>(
      getTotalArticlesGroq(LibraryPageType.CATEGORY),
      {
        categoryId,
        goalId: category.goalId,
      },
    );
    const transformFn = paginationTransformerFactory(categorySlug);
    paths.push(
      ...paginateResults({
        transformFn,
        count: categoryCount,
        pageSize: DEFAULT_PAGE_SIZE,
      }),
    );
    await Promise.all(
      FEATURED_TAG_SLUGS.map(async (tagId) => {
        const tagCount = await sanityClient.fetch<number>(
          getTotalArticlesGroq(LibraryPageType.TAG),
          {
            categoryId,
            tagId: FEATURED_TAG_SLUGS_TO_CMS_TAG[tagId] ?? '',
          },
        );
        paths.push(
          ...paginateResults({
            transformFn: paginationTransformerFactory(categorySlug, tagId),
            count: tagCount,
            pageSize: DEFAULT_PAGE_SIZE,
          }),
        );
      }),
    );
    await Promise.all(
      FEATURED_TAG_SLUGS.map(async (tagId) => {
        const tagCount = await sanityClient.fetch<number>(
          getTotalArticlesGroq(LibraryPageType.TAG),
          {
            categoryId,
            tagId: FEATURED_TAG_SLUGS_TO_CMS_TAG[tagId] ?? '',
          },
        );
        paths.push(
          ...paginateResults({
            transformFn: paginationTransformerFactory(categorySlug, tagId),
            count: tagCount,
            pageSize: DEFAULT_PAGE_SIZE,
          }),
        );
      }),
    );
  }

  return {
    paths: paths.map((params) => ({ params })),
    fallback: 'blocking',
  };
};
interface SlugsAndPage {
  slug: Nullable<string>;
  tag: Nullable<string>;
  page: number;
}

const getSlugsAndPage = (slugAndPageArray: string[]): SlugsAndPage => {
  if (slugAndPageArray.length === 3) {
    const [slug, tag, page] = slugAndPageArray;
    return {
      slug,
      tag,
      page: parseInt(page, 10),
    };
  }
  if (slugAndPageArray.length === 2) {
    const parsedValue = parseInt(slugAndPageArray[1], 10);
    if (isNaN(parsedValue)) {
      return {
        slug: slugAndPageArray[0],
        tag: slugAndPageArray[1],
        page: 1,
      };
    }
    return {
      slug: slugAndPageArray[0],
      tag: null,
      page: parseInt(slugAndPageArray[1], 10),
    };
  }
  if (slugAndPageArray.length === 1) {
    const parsedValue = parseInt(slugAndPageArray[0], 10);
    if (isNaN(parsedValue)) {
      return {
        slug: slugAndPageArray[0],
        tag: null,
        page: 1,
      };
    }
    return {
      slug: null,
      tag: null,
      page: parsedValue,
    };
  }
  return {
    slug: null,
    tag: null,
    page: 1,
  };
};

export const getStaticProps: GetStaticProps<
  Props,
  CategoryPageStaticParams
> = async (context) => {
  const { libraryPagePath } = context.params ?? {};

  const { slug, tag, page } = getSlugsAndPage(libraryPagePath ?? []);

  const layoutCategoriesHeaderData = await getStaticLayoutQuery();

  const liveFetchMetadataTypes = new Set([
    LibraryPageType.CATEGORY,
    LibraryPageType.TAG,
  ]);

  const pageType = slug
    ? slug === 'stories'
      ? LibraryPageType.STORIES
      : tag
        ? LibraryPageType.TAG
        : LibraryPageType.CATEGORY
    : LibraryPageType.BASE;

  const {
    categoryId,
    goalId,
    title: categoryTitle,
    subtitle: categorySubtitle,
  } = (liveFetchMetadataTypes.has(pageType)
    ? await sanityClient.fetch<
        Nullable<{
          categoryId: string;
          goalId: string;
          title: string;
          subtitle: string;
        }>
      >(CATEGORY_QUERY, { categorySlug: slug })
    : null) ?? {};

  // if category page and no category, or tag page and no tag, return 404
  if (
    (slug && !categoryId && liveFetchMetadataTypes.has(pageType)) ||
    (tag && !FEATURED_TAG_SLUGS_TO_CMS_TAG[tag])
  ) {
    return { notFound: true };
  }

  const title =
    pageType === LibraryPageType.CATEGORY || pageType === LibraryPageType.TAG
      ? categoryTitle
      : pageType === LibraryPageType.STORIES
        ? 'Stories'
        : 'Leland Library';
  const subtitle =
    pageType === LibraryPageType.CATEGORY
      ? categorySubtitle
      : pageType === LibraryPageType.STORIES
        ? 'Everyone has a story. Here at Leland, we want to celebrate yours.'
        : 'Expert-written articles, free resources, detailed guides, and more to help you accomplish your goals.';
  const staticProps = await getLibraryPageStaticProps(
    {
      featuredArticlesQuery: getFeaturedArticlesGroq(pageType),
      recentArticlesQuery: getRecentArticlesGroq(pageType),
      totalArticlesQuery: getTotalArticlesGroq(pageType),
    },
    {
      categoryId: categoryId ?? '',
      goalId: goalId ?? '',
      tagId: FEATURED_TAG_SLUGS_TO_CMS_TAG[tag ?? ''] ?? '',
      start: DEFAULT_PAGE_SIZE * (page - 1),
      end: DEFAULT_PAGE_SIZE * page,
    },
  );

  // if on a paginated page and no articles are found, return 404
  if (
    page > 1 &&
    !staticProps.featuredArticles?.length &&
    !staticProps.recentArticles.length
  ) {
    return { notFound: true };
  }

  return {
    props: {
      ...staticProps,
      title,
      subtitle,
      goalId: goalId ?? null,
      activeSlug: slug ?? NonCategorySlugs.HOME,
      page,
      tag,
      pageContentData: layoutCategoriesHeaderData,
    },
  };
};

export default LibraryCategoryLandingPage;
